import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import React from 'react'

import CustomTooltip from '@/app/component/atom/tooltip'
import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  Language,
  LocalAction,
  LocalQuotaActions,
  PartFamily,
  QuotaActionsMessage,
  SenderIdOption,
} from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import { GetImageProps, GetVoiceProps } from './part-config-props'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: FilesType
  id: string
  index: number
  item: QuotaActionsMessage
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  type: CampaignTemplateType
  whatsappSenderIds: SenderIdOption[]

  changeHandler: (item: Partial<QuotaActionsMessage>, changed?: boolean) => void
  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
}

const QuotaActions: React.FC<Props> = ({
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  id,
  index,
  item,
  languages,
  orgId,
  personalizationList,
  senderIds,
  timezone,
  timezones,
  token,
  type,
  whatsappSenderIds,
  changeHandler,
  getImageProps,
  getVoiceProps,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const [quotaActions, setQuotaActions] = React.useState<LocalQuotaActions>(() => fromProps(item))

  React.useEffect(() => {
    setQuotaActions(fromProps(item))
  }, [item])

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        actionNamespace: ActionContext.ActionOnly,
        campaignType: type,
        hasQuotas,
        partType: PartFamily.Action,
      }),
    [hasQuotas, type],
  )

  const handleChange = (key: keyof LocalQuotaActions, actions: LocalAction[]) => {
    const nextQuotaActions = {
      ...quotaActions,
      [key]: actions,
    }
    setQuotaActions(nextQuotaActions)
    changeHandler({
      ...item,
      quotaActions: backToProps(nextQuotaActions),
    })
  }

  const commonProps = {
    actionOptions,
    countriesTimezones,
    countryCode,
    defaultLanguage,
    defaultSenderId: SMS_ACTION_DEFAULT_SENDERID.actionsOnly,
    disableLabel: true,
    files,
    hasTranscribe: false,
    index,
    isCloseDisabled: true,
    languages,
    orgId,
    personalizationList,
    senderIds,
    timezone,
    timezones,
    token,
    transcribeEnabled: false,
    updateContactConfig: {
      invalid: false,
      openResponse: false,
      question: false,
    },
    whatsappSenderIds,
    getImageProps,
    getVoiceProps,
  }

  return (
    <div data-testid="quota-actions-container">
      <Divider sx={{ margin: '0 -24px 1rem' }} />
      <QuotaActionsSection
        actions={quotaActions.onOpen}
        description="Run the below actions if the contact is still useful for the remaining quotas."
        help="The contact is useful, if there is at least one open quota that hasn't been hit yet, that the contact qualifies for, but that the contact is not yet part of."
        voiceId={`${id}-quota-open`}
        onChange={(actions) => handleChange('onOpen', actions)}
        {...commonProps}
      />
      <Divider sx={{ my: 2 }} />
      <QuotaActionsSection
        actions={quotaActions.onExhausted}
        description="Run the below actions if the contact is no longer useful for the remaining quotas. Use this to end the survey for contacts in already hit quotas."
        help="A contact is no longer useful, if there are no quotas at all, or all quotas are either completed, the contact doesn't qualify for any, or the contact is already part of them."
        voiceId={`${id}-quota-exhausted`}
        onChange={(actions) => handleChange('onExhausted', actions)}
        {...commonProps}
      />
    </div>
  )
}

type QuotaActionsSectionProps = React.ComponentProps<typeof ActionsBox> & {
  description: string
  help: string
}

const QuotaActionsSection: React.FC<QuotaActionsSectionProps> = ({ description, help, ...actionsBoxProps }) => (
  <div>
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, mb: 1 }}>
      <Typography>{description}</Typography>
      <CustomTooltip title={help}>
        <Box component="span" sx={{ display: 'inline-flex' }} tabIndex={0}>
          <Icon aria-label="Help" color="action" fontSize="small">
            help_outline
          </Icon>
        </Box>
      </CustomTooltip>
    </Box>
    <ActionsBox {...actionsBoxProps} />
  </div>
)

const fromProps = ({ quotaActions }: QuotaActionsMessage): LocalQuotaActions => ({
  onExhausted: unwrapActions(quotaActions?.onExhausted || []),
  onOpen: unwrapActions(quotaActions?.onOpen || []),
})

const backToProps = ({ onExhausted, onOpen }: LocalQuotaActions) => ({
  onExhausted: wrapActions(onExhausted),
  onOpen: wrapActions(onOpen),
})

export default QuotaActions

export type { Props as QuotaActionsProps }
